/* eslint-disable react/no-array-index-key */
/* eslint-disable no-restricted-syntax */
/* eslint-disable max-len */
import { CircularProgress, makeStyles, Table, TableBody, TableCell, TableRow, Theme } from '@material-ui/core';
import { capitalizeAll, GenericPanelComp, useScreenSizeCheck } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import { AlertSeverity, AlertState, Entity, EntityClass, isNumericMetricInfo, NumericMetricInfo, TagMetricInfo, TimeSeriesAggregation,
    TimeSeriesResolution } from '@moderepo/mode-internal-apis';
import { CSSProperties, Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { selectEntityById, userAppDataContext, fetchEntityById, selectTimeSeriesCollectionRawData, fetchTimeSeriesCollectionRawData, setEntity,
    selectAlerts, fetchAlerts, UIFetchAlertsFilters, selectTimeSeriesCollectionData, fetchTimeSeriesCollectionData }
    from '@moderepo/mode-data-state-user-app';
import moment from 'moment';
import { AppAPI } from '@moderepo/mode-apis';
import { EntityUIComponentCollection, getMetricsDefinitionMetricsId, GridCustomSettings, isGridCustomSettings, isRootEntity,
    useSelectTranslatedEntityClasses } from '..';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { EntityUIComponentProps } from '../EntityDetailsLayouts';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingNumberValue, isValidSettingStringValue, sortObjectAttributes }
    from '../../../utils/utils';
import { NO_VALUE } from '../../../utils/constants';
import { SettingsErrorLogger } from '../../../utils/logger';
import { getTimeSeriesResolutionValueInMS } from '../../time-series/tsUtils';

const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        gridContainerStyle: {
            overflow: 'auto',
        },

        table: {
            tableLayout: 'fixed',
        },

        row: {
            '&:last-child': {
                '& $column': {
                    borderBottom: 'none',
                },
            },
        },

        column: {
            padding              : theme.spacing(1),
            overflow             : 'hidden',
            '&:not(:first-child)': {
                borderLeft: `1px solid ${theme.palette.divider}`,
            },
        },

        dataDisplay: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'space-between',
            '& .label'    : {
                fontWeight: 'bold',
            },

            '& .value-and-unit': {
                flex       : 1,
                textAlign  : 'right',
                paddingLeft: theme.spacing(1),
                '& .unit'  : {
                    paddingLeft: theme.spacing(0.5),
                },
            },
        },

        [theme.breakpoints.up('md')]: {
            column: {
                padding: theme.spacing(1, 1.5),
            },
        },

        [theme.breakpoints.up('lg')]: {
            column: {
                padding: theme.spacing(1, 2),
            },
        },
    };
}, { name: 'EntitySummaryGridUIComponent', index: 1 });


/**
 * The list of different type of entity data that this component support
 */
export enum EntityDataSource {
    // The entity data that comes from the entity's basic attributes e.g. entity's name, description, entityId, parentId, etc...
    BASIC_ATTRIBUTE = 'basic_attribute',

    // The entity data that comes from the entity's custom attributes, the attributes that come from the attribute schema
    CUSTOM_ATTRIBUTE = 'custom_attribute',

    // The entity data that comes from the entity's descendantCounts data
    DESCENDANT_COUNTS = 'descendant_counts',

    // The entity data that comes from the metrics definitions of type TSDB
    TSDB = 'tsdb',

    // The entity data that comes from the number of alert counts
    ALERT_COUNTS = 'alert_counts',
}


/**
 * For data that come from BASIC_ATTRIBUTE, the admin must specify the correct name, one of the following name in this array. If a name in the
 * config is not one of the following, the type guard function will throw an error.
 */
export const SUPPORTED_BASIC_ENTITY_ATTRIBUTES: readonly string[] = [
    'name', 'description', 'homeId', 'entityId', 'entityClass', 'entityClassName', 'parentId', 'parentName', 'ancestorId', 'ancestorName', 'image', 'icon', 'createdAt', 'updatedAt',
];

/**
 * Some entity attribute's sort field is NOT the same as the field name. For example, the 'name' field's sort field name is 'displaySettings.name'
 * and the 'description' field's sort field name is 'displaySettings.description'. Therefore, we need this map to lookup the sort field value
 * based on the field name. We only need to specify the fields that have different sortField name. For the other, we can exclude them. By default,
 * we will use the same field name as sortField.
 */
export const BASIC_ENTITY_ATTRIBUTES_SORT_FIELD_OVERRIDE_MAP: { [fieldName: string]: string} = {
    name       : 'displaySettings.name',
    description: 'displaySettings.description',
};


const DEFAULT_DATA_RESOLUTION: TimeSeriesResolution = TimeSeriesResolution.SEC_5;



/**
 * An interface to configure an entity data to display. An entity data can come from different sources. It could be an entity's basic
 * attribute such as 'name', 'description', entityId', etc... It can be one of the entity's custom attributes defined in the attribute schemas.
 * It can be data from one of the metrics definitions e.g. tsdb, home_app_proxy, etc... Therefore when we configure the component to show
 * some data, we need to tell the component where to get the value for this data.
 */
export interface EntityDataSettings {
    // To tell the component where the value for the data comes from
    readonly source: EntityDataSource;

    // The name of the data. This can be one of the basic attribute names e.g. 'name', 'description', 'homeId', 'entityId', etc...
    // If the data source is custom attributes then this will be the attribute's 'key'. If this is a data from TSDB then this will be the TSDB
    // metric's value name or tag name.
    readonly name: string;

    // Custom label to use instead of the default label
    readonly labelKey?: string | undefined;

    // The default value to show if the data does not have a value
    readonly defaultValueKey?: string | undefined;

    // The type of value. By default, the component will display the value as 'string'. If this is set to 'image', the component will assume
    // the value is an image URL and will display the value as image.
    readonly valueType?: 'image' | 'string' | undefined;

    // The component will display the data in multiple columns based on the component's "gridSizes" settings. The component will automatically
    // display the data in the order they appear in the "displayData" array. However, sometimes the admin wants a data to be displayed in a
    // specific column. The admin can use this attribute to tell the component where to place the data specifically.
    readonly preferredColumn?: number | undefined;
}


/**
 * Type guard function to make sure an object is a EntityDataSettings object
 */
export const isEntityDataSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityDataSettings => {
    const settings = obj as EntityDataSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // Make sure "source" is one of the supported data sources
    if (!isValidSettingStringValue(settings.source, true, false, Object.values(EntityDataSource))) {
        return errorLogger?.logError({
            message: `"source" is required and must be one of the following [${Object.values(
                EntityDataSource,
            ).map((dataSource) => {
                return `"${dataSource}"`;
            }).join(', ')}].`,
            value: settings.source,
            path : [...configPath, 'source'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.name, true)) {
        return errorLogger?.logError({
            message: '"name" is required and must be a non-empty string.',
            value  : settings.name,
            path   : [...configPath, 'name'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.labelKey)) {
        return errorLogger?.logError({
            message: '"labelKey" must be undefined or a non-empty string.',
            value  : settings.labelKey,
            path   : [...configPath, 'labelKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.defaultValueKey, false, true)) {
        return errorLogger?.logError({
            message: '"defaultValueKey" must be undefined or a string.',
            value  : settings.defaultValueKey,
            path   : [...configPath, 'defaultValueKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.valueType, false, true, ['image', 'string'])) {
        return errorLogger?.logError({
            message: '"valueType" must be undefined or one of these values ["image", "string"].',
            value  : settings.valueType,
            path   : [...configPath, 'valueType'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.preferredColumn, false, false, 0, 100)) {
        return errorLogger?.logError({
            message: '"preferredColumn" must be undefined or a number between 0 and 100.',
            value  : settings.preferredColumn,
            path   : [...configPath, 'preferredColumn'],
        }) ?? false;
    }

    return true;
};


/**
 * This is a more specific data settings which is used for BASIC entity data. It extends all the attributes from EntityDataSettings
 * and has its own attributes
 */
export interface BasicAttributeEntityDataSettings extends EntityDataSettings {
    readonly source: EntityDataSource.BASIC_ATTRIBUTE;
    readonly ancestorClass?: string | undefined;
}


/**
 * Type guard function to make sure an object is a BasicAttributeEntityDataSettings object
 */
export const isBasicAttributeEntityDataSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is BasicAttributeEntityDataSettings => {
    const settings = obj as BasicAttributeEntityDataSettings;
    const configPath = [...(currentConfigPath ?? [])];

    // "BasicAttributeEntityDataSettings" extends "EntityDataSettings" therefore it must satisfy the "isEntityDataSettings" type guard
    if (!isEntityDataSettings(settings, errorLogger, configPath)) {
        return false;
    }

    // Add additional checks specifically for "BasicAttributeEntityDataSettings"
    if (settings.source !== EntityDataSource.BASIC_ATTRIBUTE) {
        return errorLogger?.logError({
            message: `"source" is required and must be "${EntityDataSource.BASIC_ATTRIBUTE}".`,
            value  : settings.source,
            path   : [...configPath, 'source'],
        }) ?? false;
    }

    // Make sure "name" is one of the supported basic entity data names
    if (!isValidSettingStringValue(settings.name, true, false, SUPPORTED_BASIC_ENTITY_ATTRIBUTES)) {
        return errorLogger?.logError({
            message: `"name" is required and must be one of the following [${SUPPORTED_BASIC_ENTITY_ATTRIBUTES.map((
                attrName,
            ) => {
                return `"${attrName}"`;
            }).join(', ')}].`,
            value: settings.name,
            path : [...configPath, 'name'],
        }) ?? false;
    }

    if (settings.name === 'ancestorId' || settings.name === 'ancestorName') {
        if (!isValidSettingStringValue(settings.ancestorClass, true)) {
            return errorLogger?.logError({
                message: '"ancestorClass" is required and must be a non-empty string.',
                value  : settings.ancestorClass,
                path   : [...configPath, 'ancestorClass'],
            }) ?? false;
        }
    }

    return true;
};


/**
 * This is a more specific data settings which is used for CUSTOM entity data. It extends all the attributes from EntityDataSettings
 * and has its own attributes
 */
export interface CustomAttributeEntityDataSettings extends EntityDataSettings {
    readonly source: EntityDataSource.CUSTOM_ATTRIBUTE;

    // To specify how many decimal places to round the value to. This is only used for numeric attribute values
    readonly decimalPlaces?: number | undefined;

    // A custom unit to add next to the attribute value
    readonly unitKey?: string | undefined;
}


/**
 * Type guard function to make sure an object is a CustomAttributeEntityDataSettings object
 */
export const isCustomAttributeEntityDataSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is CustomAttributeEntityDataSettings => {
    const settings = obj as CustomAttributeEntityDataSettings;
    const configPath = [...(currentConfigPath ?? [])];

    // "CustomAttributeEntityDataSettings" extends "EntityDataSettings" therefore it must satisfy the "isEntityDataSettings" type guard
    if (!isEntityDataSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // Add additional checks specifically for "CustomAttributeEntityDataSettings"
    if (settings.source !== EntityDataSource.CUSTOM_ATTRIBUTE) {
        return errorLogger?.logError({
            message: `"source" is required and must be "${EntityDataSource.CUSTOM_ATTRIBUTE}".`,
            value  : settings.source,
            path   : [...configPath, 'source'],
        }) ?? false;
    }

    // Validate "decimalPlaces" settings
    if (!isValidSettingNumberValue(settings.decimalPlaces, false, false, 0, 10)) {
        return errorLogger?.logError({
            message: '"decimalPlaces" must be undefined or an integer between 0 and 10 inclusive',
            value  : settings.decimalPlaces,
            path   : [...configPath, 'decimalPlaces'],
        }) ?? false;
    }

    // Validate "unitKey" settings
    if (!isValidSettingStringValue(settings.unitKey)) {
        return errorLogger?.logError({
            message: '"unitKey" must be undefined or a non-empty string',
            value  : settings.unitKey,
            path   : [...configPath, 'unitKey'],
        }) ?? false;
    }

    return true;
};


/**
 * This is a more specific data settings which is used for displaying entity DESCENDANT_COUNTS. It extends all the attributes from EntityDataSettings
 * and has its own attributes
 */
export interface DescendantCountsEntityDataSettings extends EntityDataSettings {
    readonly source: EntityDataSource.DESCENDANT_COUNTS;
}

/**
 * Type guard function to make sure an object is a DescendantCountsEntityDataSettings object
 */
export const isDescendantCountsEntityDataSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is DescendantCountsEntityDataSettings => {
    const settings = obj as DescendantCountsEntityDataSettings;
    const configPath = [...(currentConfigPath ?? [])];

    // "DescendantCountsEntityDataSettings" extends "EntityDataSettings" therefore it must satisfy the "isEntityDataSettings" type guard
    if (!isEntityDataSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // Add additional checks specifically for "DescendantCountsEntityDataSettings"
    if (settings.source !== EntityDataSource.DESCENDANT_COUNTS) {
        return errorLogger?.logError({
            message: `"source" is required and must be "${EntityDataSource.DESCENDANT_COUNTS}".`,
            value  : settings.source,
            path   : [...configPath, 'source'],
        }) ?? false;
    }

    return true;
};


/**
 * This is a more specific data settings which is used for TSDB entity data. It extends all the attributes from EntityDataSettings
 * and has its own attributes
 */
export interface TSDBEntityDataSettings extends EntityDataSettings {
    readonly source: EntityDataSource.TSDB;
    readonly metricsDefinitionId: string;

    // The aggregation function to use when fetching data. The default is none, the component will fetch RAW data, if this is not defined
    readonly aggregation?: TimeSeriesAggregation | undefined;

    // The data resolution to fetch for if "aggregation" is specified. The default is "5sec" if this is not defined.
    readonly resolution?: TimeSeriesResolution | undefined;

    // A custom unit to override the metric definition unit
    readonly unitKey?: string | undefined;

    // To specify whether the metric is a tag metric. By default, the component will assume the metric is numeric and will only look for
    // the numeric metrics definition that matches the name of this settings. If this metric is TAG metric, set this to true and the
    // component will look for the metric from the tag metrics definition instead.
    readonly isTagMetric?: boolean | undefined;

    // To specify how many decimal places to round the value to. This is only used for numeric metrics and this apply to this metric only
    readonly decimalPlaces?: number | undefined;

    // The number of data point to fetch for TSDB data. By default, we fetch 1 data point but sometimes, we need to fetch more than 1
    // because the latest data can be `null`. If we fetch more than 1 data points, we can go through the response data and use the last
    // non-null data point.
    readonly sampleSize?: number | undefined;
}


export const isTSDBEntityDataSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is TSDBEntityDataSettings => {
    const settings = obj as TSDBEntityDataSettings;
    const configPath = [...(currentConfigPath ?? [])];

    // TSDBEntityDataSettings extends "EntityDataSettings" therefore it must satisfy the "isEntityDataSettings" type guard
    if (!isEntityDataSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // Add additional checks specifically for "TSDBEntityDataSettings"
    if (settings.source !== EntityDataSource.TSDB) {
        return errorLogger?.logError({
            message: `"source" is required and must be "${EntityDataSource.TSDB}".`,
            value  : settings.source,
            path   : [...configPath, 'source'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.metricsDefinitionId, true)) {
        return errorLogger?.logError({
            message: '"metricsDefinitionId" is required and must be a non-empty string.',
            value  : settings.metricsDefinitionId,
            path   : [...configPath, 'metricsDefinitionId'],
        }) ?? false;
    }

    // aggregation is optional but if it is provided, it must be one of the TimeSeriesAggregation
    const supportedAggregations = [...Object.values(TimeSeriesAggregation)];
    if (!isValidSettingStringValue(settings.aggregation, false, false, supportedAggregations)) {
        return errorLogger?.logError({
            message: `"aggregation" must be undefined or one of the following values [${
                supportedAggregations.map((type) => {
                    return `"${type}"`;
                }).join(', ')}]`,
            value: settings.aggregation,
            path : [...configPath, 'aggregation'],
        }) ?? false;
    }

    // aggregation is optional but if it is provided, it must be one of the TimeSeriesResolution
    const supportedResolutions = [...Object.values(TimeSeriesResolution)];
    if (!isValidSettingStringValue(settings.resolution, false, false, supportedResolutions)) {
        return errorLogger?.logError({
            message: `"resolution" must be undefined or one of the following values [${
                supportedResolutions.map((type) => {
                    return `"${type}"`;
                }).join(', ')}]`,
            value: settings.resolution,
            path : [...configPath, 'resolution'],
        }) ?? false;
    }

    // Validate "unitKey" settings
    if (!isValidSettingStringValue(settings.unitKey, false, true)) {
        return errorLogger?.logError({
            message: '"unitKey" must be undefined or a string',
            value  : settings.unitKey,
            path   : [...configPath, 'unitKey'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.isTagMetric)) {
        return errorLogger?.logError({
            message: '"isTagMetric" must be undefined or a boolean.',
            value  : settings.isTagMetric,
            path   : [...configPath, 'isTagMetric'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.decimalPlaces, false, false, 0, 10)) {
        return errorLogger?.logError({
            message: '"decimalPlaces" must be undefined or an integer between 0 and 10 inclusive',
            value  : settings.decimalPlaces,
            path   : [...configPath, 'decimalPlaces'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.sampleSize, false, false, 1, 100)) {
        return errorLogger?.logError({
            message: '"sampleSize" must be undefined or an integer between 1 and 100 inclusive',
            value  : settings.sampleSize,
            path   : [...configPath, 'sampleSize'],
        }) ?? false;
    }


    return true;
};


/**
 * This is a more specific data settings which is used for ALERT_COUNTS entity data. It extends all the attributes from EntityDataSettings
 * and has its own attributes.
 *
 * NOTE: Unlike other EntityDataSettings type, the AlertCountsEntityDataSettings's "name" attribute is not important. The can be anything as
 * long as the name is a unique string among other AlertCountsEntityDataSettings
 */
export interface AlertCountsEntityDataSettings extends EntityDataSettings {
    readonly source: EntityDataSource.ALERT_COUNTS;

    // To specify which type of alert severity to show e.g. HIGH severity alerts, MODERATE severity alerts, LOW severity alerts, or all.
    // The default is ALL if this is not specified.
    readonly severity?: AlertSeverity | undefined;

    // To specify which type of alert to show, only active alerts, recovered alerts, etc... The default is show the count of all
    // alerts if this is not specified.
    readonly state?: AlertState | undefined;
}


/**
 * Type guard function to check if a settings object is a AlertCountsEntityDataSettings
 */
export const isAlertCountsEntityDataSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is AlertCountsEntityDataSettings => {
    const settings = obj as AlertCountsEntityDataSettings;
    const configPath = [...(currentConfigPath ?? [])];

    // AlertCountsEntityDataSettings extends "EntityDataSettings" therefore it must satisfy the "isEntityDataSettings" type guard
    if (!isEntityDataSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    const supportedAlertSeverities = [AlertSeverity.HIGH, AlertSeverity.MODERATE, AlertSeverity.LOW];
    if (!isValidSettingNumberValue(settings.severity, false, false) || (settings.severity && !supportedAlertSeverities.includes(settings.severity))) {
        return errorLogger?.logError({
            message: `"severity" must be undefined or one of these alert severity numbers [${supportedAlertSeverities.map((value) => {
                return `${value}`;
            }).join(', ')}]`,
            value: settings.severity,
            path : [...configPath, 'severity'],
        }) ?? false;
    }

    const supportedAlertStates = [AlertState.ALERTING, AlertState.RECOVERED, AlertState.OBSOLETE, AlertState.INEFFECTIVE];
    if (!isValidSettingNumberValue(settings.state, false, false) || (settings.state && !supportedAlertStates.includes(settings.state))) {
        return errorLogger?.logError({
            message: `"state" must be undefined or one of these alert state numbers [${supportedAlertStates.map((value) => {
                return `${value}`;
            }).join(', ')}]`,
            value: settings.state,
            path : [...configPath, 'state'],
        }) ?? false;
    }

    return true;
};

/**
 * type guard function to check if an object is one of the subtypes of EntityDataSettings
 */
export const isSubTypeEntityDataSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is BasicAttributeEntityDataSettings | CustomAttributeEntityDataSettings | TSDBEntityDataSettings
| DescendantCountsEntityDataSettings | AlertCountsEntityDataSettings => {

    const settings = obj as EntityDataSettings;
    const configPath = [...(currentConfigPath ?? [])];

    // Return "true" if dataSettings is one of the following type of EntityDataSettings.
    if (
        settings.source === EntityDataSource.CUSTOM_ATTRIBUTE
            && !isCustomAttributeEntityDataSettings(settings, errorLogger, [...configPath])
    ) {
        return false;
    }

    if (
        settings.source === EntityDataSource.TSDB
            && !isTSDBEntityDataSettings(settings, errorLogger, [...configPath])
    ) {
        return false;
    }

    if (
        settings.source === EntityDataSource.BASIC_ATTRIBUTE
            && !isBasicAttributeEntityDataSettings(settings, errorLogger, [...configPath])
    ) {
        return false;
    }

    if (
        settings.source === EntityDataSource.ALERT_COUNTS
            && !isAlertCountsEntityDataSettings(settings, errorLogger, [...configPath])
    ) {
        return false;
    }

    // Call "isEntityDataSettings" to validate the common attributes e.g. name, source
    if (!isEntityDataSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};


/**
 * Once we fetched entity data based on the EntityDataSettings, we will create instance of an EntityData object to store the data to be displayed
 */
export interface EntityData {
    // The label of the data which can come from different sources. If the data comes from basic attributes then the label comes from the
    // translations. If data comes from custom attribute then the label will come from the attribute schema's label. If data comes from TSDB
    // then the label will come from the TSDB metric definition's displayName.
    readonly label: string;
    readonly value: string | number | boolean | undefined;
    readonly unit: string | undefined;

    // Keep reference to the original entity data settings so that we can use it when we need it.
    readonly settings: EntityDataSettings;
}



/**
 * This is the interface for the EntitySummaryGridUIComponent's CustomSettings.
 */
export interface EntitySummaryGridUIComponentCustomSettings extends GridCustomSettings {
    // Custom title and icon
    readonly titleKey?: string | undefined;
    readonly icon?: string | undefined;

    // To specify how many decimal places to round the value to. This is global setting that apply to all data EXCEPT for data that have
    // their own custom decimalPlaces setting.
    readonly decimalPlaces?: number | undefined;

    // The default value to display when an data does not have a value. This is a global setting that apply to all data EXCEPT for data that
    // have their own defaultValueKey setting.
    readonly defaultValueKey?: string | undefined;

    // An array of data to be displayed. The data will be displayed in the same order as they appear in this array.
    readonly displayData: readonly EntityDataSettings[];

    // The custom CSS style for the outer container element
    readonly containerStyle?: CSSProperties | undefined;

    // The custom CSS style for the outer table element
    readonly gridContainerStyle?: CSSProperties | undefined;

    // The custom CSS style for the data container element, the TableCell
    readonly dataContainerStyle?: CSSProperties | undefined;
}


/**
 * Type guard function to validate the obj to make sure it is a type of EntitySummaryGridUIComponentCustomSettings
 */
export const isEntitySummaryGridUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntitySummaryGridUIComponentCustomSettings => {
    const settings = obj as EntitySummaryGridUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey)) {
        return errorLogger?.logError({
            message: '"titleKey" must be undefined or a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.icon)) {
        return errorLogger?.logError({
            message: '"icon" must be undefined or a non-empty string.',
            value  : settings.icon,
            path   : [...configPath, 'icon'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.decimalPlaces, false, false, 0, 10)) {
        return errorLogger?.logError({
            message: '"decimalPlaces" must be undefined or an integer between 0 and 10 inclusive',
            value  : settings.decimalPlaces,
            path   : [...configPath, 'decimalPlaces'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.defaultValueKey, false, true)) {
        return errorLogger?.logError({
            message: '"defaultValueKey" must be undefined or a string.',
            value  : settings.defaultValueKey,
            path   : [...configPath, 'defaultValueKey'],
        }) ?? false;
    }

    if (settings.containerStyle !== undefined && !isObjectLiteral(settings.containerStyle)) {
        return errorLogger?.logError({
            message: '"containerStyle" must be undefined or a CSSProperties object.',
            value  : settings.containerStyle,
            path   : [...configPath, 'containerStyle'],
        }) ?? false;
    }

    if (settings.gridContainerStyle !== undefined && !isObjectLiteral(settings.gridContainerStyle)) {
        return errorLogger?.logError({
            message: '"gridContainerStyle" must be undefined or a CSSProperties object.',
            value  : settings.gridContainerStyle,
            path   : [...configPath, 'gridContainerStyle'],
        }) ?? false;
    }

    if (settings.dataContainerStyle !== undefined && !isObjectLiteral(settings.dataContainerStyle)) {
        return errorLogger?.logError({
            message: '"dataContainerStyle" must be undefined or a CSSProperties object.',
            value  : settings.dataContainerStyle,
            path   : [...configPath, 'dataContainerStyle'],
        }) ?? false;
    }

    if (!isGridCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }


    // Validate the "displayData" array
    if (!(Array.isArray(settings.displayData)) || settings.displayData.length === 0) {
        return errorLogger?.logError({
            message: '"displayData" is required and must be a non-empty array.',
            value  : settings.displayData,
            path   : [...configPath, 'displayData'],
        }) ?? false;
    }


    // Make sure every element in the "displayData" are valid
    if (!settings.displayData.every((dataSettings, index) => {
        // Return "true" if dataSettings is one of the following type of EntityDataSettings.
        if (!isSubTypeEntityDataSettings(dataSettings, errorLogger, [...configPath, 'displayData', `[${index}]`])) {
            return false;
        }

        return true;
    })) {
        return false;
    }

    return true;
};



/**
 * Custom hook to get multiple entity's basic attribute data e.g. name, description, parentId, homeId, etc... for multiple entities
 * Since we already have the entities object, this function is mostly straightforward because all we do is get the values from the entity objects'
 * attributes. HOWEVER, one of the attributes that makes this function a little complicated is the "parentName". To be able to get the entity
 * parent's name, we will need to fetch entity object for the parent.
 */
const useGetEntitiesBasicAttributeData = (
    projectId: number,
    entities: readonly Entity[] | undefined,
    entityClasses: readonly EntityClass[] | undefined,
    multipleDataSettings: readonly EntityDataSettings[] | undefined,
): {
    readonly [entityId: string]: readonly EntityData[] | undefined;
} | undefined => {

    const project = useSelectProject(projectId);
    const { trans } = useRitzTranslation(project);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    // The state to keep track of the params needed for fetching missing data
    const [paramsForFetchingMissingData, setParamsForFetchingMissingData] = useState<readonly {
        readonly entityId: string;
    }[] | undefined>();

    const [hasErrors, setHasErrors] = useState<boolean>(false);


    /**
     * Fetch the ancestors for a given entity and cache them so that we have data ready to use
     */
    const fetchEntityAncestors = useCallback(async (currentEntity: Entity) => {
        /**
         * Given an entity, recursively fetch it's parent, then grandparent, and so on until we hit the root entity. Get all the parent entities
         * we fetched into the result array
         */
        const fetchParentEntityRecursive = async (projectId: number, entity: Entity, result: Entity[]): Promise<Entity[]> => {
            if (isRootEntity(entity)) {
                // We are done
                return result;
            }

            // If we are not at the root, make an API call to fetch the entity's parent and add the parent entity to the beginning of the result
            // But first, check if we already have the parent entity in cache
            const parent = selectEntityById(appDataState, projectId, entity.parentId as string)   // get from cache
                || await AppAPI.getInstance().getEntityById(projectId, entity.parentId as string);      // if not in cache, make API call

            // Since we called the API directly to fetch entity data, we should add the fetched entity to the data state so that we can reuse it
            appDataDispatch(setEntity(projectId, parent));

            result.unshift(parent);

            // Recursively call fetchParentEntityRecursive again to fetch the parent of the new parent entity
            return fetchParentEntityRecursive(projectId, parent, result);
        };

        await fetchParentEntityRecursive(projectId, currentEntity, [currentEntity]);
    }, [appDataState, appDataDispatch, projectId]);


    /**
     * This useEffect is used for fetching the entity's ancestors if one of the dataSettings needs to get data for an ancestor
     */
    useEffect(() => {
        if (entities && multipleDataSettings) {
            const requireFetchingAncestors = multipleDataSettings.some((dataSettings) => {
                return isBasicAttributeEntityDataSettings(dataSettings) && (dataSettings.name === 'ancestorName' || dataSettings.name === 'ancestorId');
            });
            if (requireFetchingAncestors) {
                entities.forEach((entity) => {
                    fetchEntityAncestors(entity);
                });
            }
        }
    }, [entities, fetchEntityAncestors, multipleDataSettings]);


    /**
     * This function will loop through the "multipleDataSettings" array, get the value for each data setting, and return a EntityData object
     * for each data settings.
     * NOTE that this function will only process data settings which has "source" of type "basic_attribute"
     */
    const multipleEntityData = useMemo(() => {

        if (entities && entityClasses && multipleDataSettings) {


            // Create a Map of entityId => Array of "EntityDataDisplay" for storing the result for each entity
            const result: {
                [entityId: string]: EntityData[];
            } = {};

            // Create a new array of params for fetching missing data.
            const newParamsForFetchingMissingData: {
                readonly entityId: string;
            }[] = [];

            for (const dataSettings of multipleDataSettings) {

                // This function only handle dataSettings of type BasicAttributeEntityDataSettings
                if (isBasicAttributeEntityDataSettings(dataSettings)) {

                    entities.forEach((entity) => {
                        const entityClass = entityClasses.find((entityClass) => {
                            return entityClass.entityClass === entity.entityClass;
                        });

                        // Get the value for the data from the entity's object
                        const value = (() => {
                            switch (dataSettings.name) {
                                case 'name':
                                    return entity.displaySettings.name;
                                case 'description':
                                    return entity.displaySettings.description;
                                case 'homeId':
                                    return entity.homeId.toString();
                                case 'entityId':
                                    return entity.entityId;
                                case 'entityClass':
                                    return entity.entityClass;
                                case 'entityClassName':
                                    return entityClass?.displaySettings.name ?? entity.entityClass;
                                case 'parentId':
                                    // Root entities.parentId will be 'root' and we don't want to display 'root', we will display nothing instead.
                                    if (!isRootEntity(entity)) {
                                        return entity.parentId;
                                    }
                                    return undefined;
                                case 'parentName':
                                    if (!isRootEntity(entity) && entity.parentId) {
                                        // Find the parent entity object from cache. If there is one, return the parent's name
                                        const parent = selectEntityById(appDataState, projectId, entity.parentId);
                                        if (parent) {
                                            return parent.displaySettings.name;
                                        }

                                        // "parent" data is not available yet. Add a new action to fetch missing data HOWEVER, only do it if we have not
                                        // fetched it before. If we have tried fetching data before and got errors then don't create a new action
                                        // because it will fail again anyway.
                                        if (!hasErrors) {
                                            newParamsForFetchingMissingData.push({
                                                entityId: entity.parentId,
                                            });
                                        }
                                    }
                                    return undefined;
                                case 'ancestorId':
                                case 'ancestorName':
                                    let currentParent = entity.parentId ? selectEntityById(appDataState, projectId, entity.parentId) : undefined;
                                    while (
                                        currentParent && !isRootEntity(currentParent) && currentParent.parentId
                                    && currentParent.entityClass !== dataSettings.ancestorClass
                                    ) {
                                        currentParent = selectEntityById(appDataState, projectId, currentParent.parentId);
                                    }
                                    if (currentParent && currentParent.entityClass === dataSettings.ancestorClass) {
                                        if (dataSettings.name === 'ancestorId') {
                                            return currentParent.entityId;
                                        }
                                        return currentParent.displaySettings.name;
                                    }
                                    return undefined;
                                case 'image':
                                    return entity.displaySettings.imageData?.url;
                                case 'icon':
                                    return entity.displaySettings.iconData?.url;
                                case 'createdAt':
                                    return entity.createdAt;
                                case 'updatedAt':
                                    return entity.updatedAt;
                                default:
                                    return undefined;
                            }
                        })();

                        // Get the data's label
                        const label = (() => {
                            // If custom label is provided then use the custom label
                            if (dataSettings.labelKey !== undefined) {
                                return trans(dataSettings.labelKey);
                            }

                            switch (dataSettings.name) {
                                case 'name':
                                    return capitalizeAll(trans('field.name.label'));
                                case 'description':
                                    return capitalizeAll(trans('field.description.label'));
                                case 'homeId':
                                    return capitalizeAll(trans('field.home_id.label'));
                                case 'entityId':
                                    return capitalizeAll(trans('field.entity_id.label'));
                                case 'entityClass':
                                case 'entityClassName':
                                    return capitalizeAll(trans('field.entity_class.label'));
                                case 'parentId':
                                case 'ancestorId':
                                    return capitalizeAll(trans('field.parent_entity_id.label'));
                                case 'parentName':
                                case 'ancestorName':
                                    return capitalizeAll(trans('field.parent_entity.label'));
                                default:
                                    return dataSettings.name;
                            }
                        })();

                        // Get the EntityData array for the current entity. Create a new array if one does not exist yet
                        const oneEntityDataArray = result[entity.entityId] ?? [];
                        result[entity.entityId] = oneEntityDataArray;

                        oneEntityDataArray.push({
                            settings: dataSettings,
                            label   : label ?? dataSettings.name,
                            value,
                            unit    : undefined,
                        });
                    });
                }
            }


            // Set the newParamsForFetchingMissingData to remember that we need to fetch these missing data. But we only set it if the
            // newParamsForFetchingMissingData is different from the current paramsForFetchingMissingData
            setParamsForFetchingMissingData((currentParams) => {
                if (!currentParams
                  || JSON.stringify(sortObjectAttributes(currentParams)) !== JSON.stringify(sortObjectAttributes(newParamsForFetchingMissingData))
                ) {
                    return newParamsForFetchingMissingData;
                }
                return currentParams;
            });


            // If we don't have any missing data to fetch then we can return whatever we have in the result
            // Else, return undefined and wait for more data to be fetched
            if (newParamsForFetchingMissingData.length === 0) {
                return result;
            }
        }

        return undefined;
    }, [appDataState, entities, entityClasses, hasErrors, multipleDataSettings, projectId, trans]);


    /**
     * This useEffect is used for checking the list of "paramsForFetchingMissingData" to see if there are any actions need to be dispatched
     * and dispatch them.
     */
    useEffect(() => {
        if (paramsForFetchingMissingData && paramsForFetchingMissingData.length > 0) {
            const actions = paramsForFetchingMissingData;
            actions.forEach((action) => {
                appDataDispatch(fetchEntityById(projectId, action.entityId)).catch(() => {
                    setHasErrors(true);
                });
            });
        }
    }, [appDataDispatch, paramsForFetchingMissingData, projectId]);

    return multipleEntityData;
};



/**
 * Custom hook to get multiple entity's custom attribute data.
 * Since we already have the entity object, this function is straightforward because all we do is get the values from the entity.attributes object.
 * There is no need to fetch additional data.
 */
const useGetEntitiesCustomAttributeData = (
    projectId: number,
    entities: readonly Entity[] | undefined,
    entityClasses: readonly EntityClass[] | undefined,
    multipleDataSettings: readonly EntityDataSettings[] | undefined,
): {
    readonly [entityId: string]: readonly EntityData[] | undefined;
} | undefined => {

    const project = useSelectProject(projectId);
    const { trans } = useRitzTranslation(project);

    /**
     * This function will loop through the "multipleDataSettings" array, get the value for each data setting, and return a EntityData object
     * for each data settings.
     * NOTE that this function will only process data settings which has "source" of type "custom_attribute"
     */
    const multipleEntityData = useMemo(() => {

        if (entities && entityClasses && multipleDataSettings) {

            // Create an map of entityId => Array of "EntityDataDisplay" for storing the result for each entity
            const result: {
                [entityId: string]: EntityData[];
            } = {};

            for (const dataSettings of multipleDataSettings) {

                entities.forEach((entity) => {
                    const entityClass = entityClasses.find((entityClass) => {
                        return entityClass.entityClass === entity.entityClass;
                    });

                    // This function only handle dataSettings of type CustomAttributeEntityDataSettings
                    if (isCustomAttributeEntityDataSettings(dataSettings)) {

                        const value = (() => {
                            const value = entity.attributes?.[dataSettings.name];
                            if (value !== undefined) {
                                if (typeof value === 'object') {
                                    // Value must be an Object or Array
                                    return JSON.stringify(value);
                                }
                                if (typeof value === 'boolean') {
                                    // Value is a boolean so return a string representation of the value "true" or "false"
                                    return value.toString();
                                }
                                // Value is either string or number so return the value as is
                                return value;
                            }
                            return undefined;
                        })();

                        const label = (() => {
                            // If custom label is provided then use the custom label
                            if (dataSettings.labelKey !== undefined) {
                                return trans(dataSettings.labelKey);
                            }

                            // Find the schema param where the attribute came from
                            const param = entityClass?.attributeSchema?.find((attrParam) => {
                                return attrParam.key === dataSettings.name;
                            });

                            // Use the param's label as the data label.
                            return param?.label;
                        })();

                        // Get the EntityData array for the current entity. Create a new array if one does not exist yet
                        const oneEntityDataArray = result[entity.entityId] ?? [];
                        result[entity.entityId] = oneEntityDataArray;

                        oneEntityDataArray.push({
                            settings: dataSettings,
                            label   : label ?? dataSettings.name,
                            value,
                            unit    : dataSettings.unitKey ? trans(dataSettings.unitKey) : undefined,
                        });
                    }
                });
            }

            return result;
        }

        return undefined;
    }, [entities, entityClasses, multipleDataSettings, trans]);

    return multipleEntityData;
};


/**
 * Custom hook to get multiple entity's descendantCounts data.
 * Since we already have the entity object, this function is straightforward because all we do is get the values from the entity.descendantCounts
 * object. There is no need to fetch additional data.
 */
const useGetEntitiesDescendantCountsData = (
    projectId: number,
    entities: readonly Entity[] | undefined,
    entityClasses: readonly EntityClass[] | undefined,
    multipleDataSettings: readonly EntityDataSettings[] | undefined,
): {
    readonly [entityId: string]: readonly EntityData[] | undefined;
} | undefined => {

    const project = useSelectProject(projectId);
    const { trans } = useRitzTranslation(project);

    /**
     * This function will loop through the "multipleDataSettings" array, get the value for each data setting, and return a EntityData object
     * for each data settings.
     * NOTE that this function will only process data settings which has "source" of type "descendant_counts"
     */
    const multipleEntityData = useMemo(() => {

        if (entities && entityClasses && multipleDataSettings) {

            // Create an map of entityId => Array of "EntityDataDisplay" for storing the result for each entity
            const result: {
                [entityId: string]: EntityData[];
            } = {};

            for (const dataSettings of multipleDataSettings) {

                // eslint-disable-next-line no-loop-func
                entities.forEach((entity) => {

                    // This function only handle dataSettings of type DescendantCountsEntityDataSettings
                    if (isDescendantCountsEntityDataSettings(dataSettings)) {

                        const value = entity.descendantCounts?.[dataSettings.name];

                        const label = (() => {
                            // If custom label is provided then use the custom label
                            if (dataSettings.labelKey !== undefined) {
                                return trans(dataSettings.labelKey);
                            }

                            // Else use the entityClass' displayName
                            // Find the entity class for the entity that we are trying to display the count for
                            // We will use the EntityClass' displayName for the label.
                            const entityClass = entityClasses.find((entityClass) => {
                                return entityClass.entityClass === dataSettings.name;
                            });

                            return entityClass?.displaySettings.name;
                        })();

                        // Get the EntityData array for the current entity. Create a new array if one does not exist yet
                        const oneEntityDataArray = result[entity.entityId] ?? [];
                        result[entity.entityId] = oneEntityDataArray;

                        oneEntityDataArray.push({
                            settings: dataSettings,
                            label   : label ?? dataSettings.name,
                            value,
                            unit    : undefined,
                        });
                    }
                });
            }

            return result;
        }

        return undefined;
    }, [entities, entityClasses, multipleDataSettings, trans]);

    return multipleEntityData;
};


interface FetchMissingCollectionDataParams {
    readonly multipleDataSettings: TSDBEntityDataSettings[];
    readonly metricsInfo: (NumericMetricInfo | TagMetricInfo | undefined)[];
    readonly homeId: number;
    readonly tsdbModuleId: string;
    readonly collectionId: string;
    readonly aggregation: TimeSeriesAggregation | undefined;
    readonly valueNames: string[];
    readonly tagNames: string[];
    readonly sampleSize: number;
}

interface FetchMissingCollectionRawDataParams extends FetchMissingCollectionDataParams {
    readonly timestamp: string;
}

interface FetchMissingCollectionAggregatedDataParams extends FetchMissingCollectionDataParams {
    readonly startTime: string;
    readonly endTime: string;
    readonly resolution: TimeSeriesResolution;
}

// Type guard functions to check if an Object is a FetchMissingCollectionRawDataParams or FetchMissingCollectionAggregatedDataParams
const isFetchMissingCollectionRawDataParams = (obj: unknown): obj is FetchMissingCollectionRawDataParams => {
    return (obj as FetchMissingCollectionDataParams).aggregation === undefined;
};



/**
 * Custom hook to get multiple entity's collection data. This will require making API call to fetch collection data. The tricky part of this
 * function is that we need to optimize it. We can't simply go through each "dataSettings" object 1 at a time and fetching data for each
 * object separately. It is possible that multiple dataSettings object's data come from the same collection therefore we only need to make
 * 1 API call to fetch them. For example, let's say we have an array of data settings like this
 *      displayData: [{
 *          source: "tsdb",
 *          name: "temperature",
 *          metricsDefinitionId: "environment"
 *      }, {
 *          source: "tsdb",
 *          name: "pressure",
 *          metricsDefinitionId: "environment"
 *      }, {
 *          source: "tsdb",
 *          name: "humidity",
 *          metricsDefinitionId: "environment"
 *      }]
 *
 * All those 3 data come from the same metrics definition which mean they are in the same collection. So when we fetch data for these 3 dataSettings,
 * we only need to make 1 API call.
 *
 * So for optimization, we need to go through all the dataSettings and group all the dataSettings that belonged to the same collection together in
 * 1 group. And then we go through each group and make 1 API call to fetch data for the entire group.
 */
const useGetEntitiesTSDBData = (
    projectId: number,
    entities: readonly Entity[] | undefined,
    entityClasses: readonly EntityClass[] | undefined,
    multipleDataSettings: readonly EntityDataSettings[] | undefined,
    date: number,
): {
    readonly [entityId: string]: readonly EntityData[] | undefined;
} | undefined => {
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = useSelectProject(projectId);
    const { trans } = useRitzTranslation(project);

    // The state to keep track of the params needed for fetching missing data
    const [paramsForFetchingMissingData, setParamsForFetchingMissingData] = useState<(
        FetchMissingCollectionRawDataParams | FetchMissingCollectionAggregatedDataParams
    )[] | undefined>();

    const [hasErrors, setHasError] = useState<boolean>(false);


    /**
     * This function will loop through the "multipleDataSettings" array, get the value for each data setting, and return a EntityData object
     * for each data settings.
     * NOTE that this function will only process data settings which has "source" of type "tsdb"
     */
    const multipleEntityData = useMemo(() => {

        if (entities && entityClasses && multipleDataSettings) {

            // Create an map of entityId => Array of "EntityDataDisplay" for storing the result for each entity
            const result: {
                [entityId: string]: EntityData[];
            } = {};

            // Create a new array of params for fetching missing data.
            const newParamsForFetchingMissingData: (FetchMissingCollectionRawDataParams | FetchMissingCollectionAggregatedDataParams)[] = [];

            entities.forEach((entity) => {
                const entityClass = entityClasses.find((entityClass) => {
                    return entityClass.entityClass === entity.entityClass;
                });


                // collect a list of Collections we need to get data for. Group all the dataSettings that has data from the same collection into
                // the same group using the "collectionId" as group key.
                const allCollectionGroups: {
                    [collectionId: string]: FetchMissingCollectionRawDataParams | FetchMissingCollectionAggregatedDataParams;
                } = {};

                for (const dataSettings of multipleDataSettings) {

                    // This function only handle dataSettings of type TSDBEntityDataSettings
                    if (isTSDBEntityDataSettings(dataSettings)) {
                        const metricsDefinition = entityClass?.metricsDefinitions?.find((metricDef) => {
                            return metricDef.metricsDefinitionId === dataSettings.metricsDefinitionId;
                        });
                        if (metricsDefinition) {
                            const collectionId = getMetricsDefinitionMetricsId(entity, metricsDefinition.metricsDefinitionId);
                            const numericMetricInfo = !dataSettings.isTagMetric ? metricsDefinition.numericMetrics?.find((metricInfo) => {
                                return metricInfo.name === dataSettings.name;
                            }) : undefined;
                            const tagMetricInfo = dataSettings.isTagMetric === true ? metricsDefinition.tagMetrics?.find((metricInfo) => {
                                return metricInfo.name === dataSettings.name;
                            }) : undefined;

                            // Create a data groupId by combining the collectionId with the sampleSize. So if 2 data come from the same
                            // collection BUT they have different sample size, they will be in 2 different group which mean we will
                            // make different API call to fetch their data.
                            const groupId = `${collectionId}`
                                            + `-agg-${dataSettings.aggregation ?? 'raw'}`
                                            + `-res-${dataSettings.resolution ?? (dataSettings.aggregation ? DEFAULT_DATA_RESOLUTION : 'now')}`
                                            + `-size-${dataSettings.sampleSize ?? 1}`;

                            const collectionGroup = allCollectionGroups[groupId];
                            if (collectionGroup) {
                                // if the collectionGroup already existed, add the dataSettings to the info object
                                collectionGroup.multipleDataSettings.push(dataSettings);
                                if (!dataSettings.isTagMetric) {
                                    collectionGroup.valueNames.push(dataSettings.name);
                                    collectionGroup.metricsInfo.push(numericMetricInfo);
                                } else if (dataSettings.isTagMetric) {
                                    collectionGroup.tagNames.push(dataSettings.name);
                                    collectionGroup.metricsInfo.push(tagMetricInfo);
                                }
                            } else {
                                // create a new collectionGroup using the collectionId as key
                                // eslint-disable-next-line no-lonely-if
                                if (dataSettings.aggregation === undefined) {
                                    allCollectionGroups[groupId] = {
                                        homeId              : entity.homeId,
                                        tsdbModuleId        : metricsDefinition.dataStoreConfiguration.moduleInstanceId,
                                        collectionId,
                                        timestamp           : moment(date).toISOString(),
                                        valueNames          : !dataSettings.isTagMetric ? [dataSettings.name] : [],
                                        tagNames            : dataSettings.isTagMetric ? [dataSettings.name] : [],
                                        aggregation         : undefined,
                                        multipleDataSettings: [dataSettings],
                                        metricsInfo         : [numericMetricInfo ?? tagMetricInfo],
                                        sampleSize          : dataSettings.sampleSize ?? 1,
                                    };
                                } else {
                                    // Get the resolution string e.g. '5sec', '1min', '1week', etc... and convert it to value in milliseconds.
                                    // For example: 5sec => 5000, 1min => 60000
                                    const resolution = dataSettings.resolution ?? DEFAULT_DATA_RESOLUTION;
                                    const resolutionValueInMS = getTimeSeriesResolutionValueInMS(resolution);
                                    const sampleSize = dataSettings.sampleSize ?? 1;

                                    allCollectionGroups[groupId] = {
                                        homeId              : entity.homeId,
                                        tsdbModuleId        : metricsDefinition.dataStoreConfiguration.moduleInstanceId,
                                        collectionId,
                                        // The startTime will be N resolutions before the "date". For example: If resolution is 1 hour and the admin
                                        // wants 5 sampleSize then we will fetch data starting from 5 hours before the "date"
                                        startTime           : moment(date).subtract(resolutionValueInMS * sampleSize, 'ms').toISOString(),
                                        endTime             : moment(date).toISOString(),
                                        valueNames          : !dataSettings.isTagMetric ? [dataSettings.name] : [],
                                        tagNames            : dataSettings.isTagMetric ? [dataSettings.name] : [],
                                        aggregation         : dataSettings.aggregation ?? TimeSeriesAggregation.AVG,
                                        resolution,
                                        multipleDataSettings: [dataSettings],
                                        metricsInfo         : [numericMetricInfo ?? tagMetricInfo],
                                        sampleSize          : dataSettings.sampleSize ?? 1,
                                    };
                                }
                            }
                        }
                    }
                }


                // Now for each collectionGroup, get the data for the metrics in that collectionGroup
                for (const collectionGroup of Object.values(allCollectionGroups)) {

                    // Get collection data from cache
                    const collectionData = isFetchMissingCollectionRawDataParams(collectionGroup) ? selectTimeSeriesCollectionRawData(
                        appDataState,
                        entity.homeId,
                        collectionGroup.tsdbModuleId,
                        collectionGroup.collectionId,
                        collectionGroup.timestamp,
                        -collectionGroup.sampleSize,
                        collectionGroup.valueNames,
                        collectionGroup.tagNames,
                    ) : selectTimeSeriesCollectionData(
                        appDataState,
                        entity.homeId,
                        collectionGroup.tsdbModuleId,
                        collectionGroup.collectionId,
                        collectionGroup.startTime,
                        collectionGroup.endTime,
                        collectionGroup.valueNames,
                        collectionGroup.aggregation,
                        collectionGroup.resolution,
                    );

                    // There is no data from cache for the collection, we probably haven't make API call to fetch it yet.
                    // Add it to the list of data we need to fetch. However, we will only do this if !hasErrors because if hasErrors is true,
                    // it means we have already tried to fetch collection data but failed therefore don't need to try again.
                    if (!collectionData && !hasErrors) {
                        newParamsForFetchingMissingData.push(collectionGroup);

                        // Stop the loop. Let's wait for the data to be fetched and then continue again
                        break;
                    }

                    collectionGroup.multipleDataSettings.forEach((dataSettings, index) => {
                        if (isTSDBEntityDataSettings(dataSettings)) {
                            const metricInfo = collectionGroup.metricsInfo[index];

                            // Find the first non-null value from the collection data starting from the LAST row. We need to go backward because
                            // we want to show the latest data.
                            const value = (() => {
                                if (collectionData && collectionData.data) {
                                    for (let i = collectionData.data.length - 1; i >= 0; i -= 1) {
                                        // The value for the dataSettings at index i is at column i + 1 because collection data row has
                                        // "date" column at index 0
                                        const value = collectionData.data[i][index + 1];
                                        if (value !== undefined && value !== null) {
                                            return value;
                                        }
                                    }
                                }
                                return undefined;
                            })();

                            // If custom label is provided then use the custom label
                            const label = dataSettings.labelKey !== undefined
                                ? trans(dataSettings.labelKey)
                                : (metricInfo as NumericMetricInfo)?.evaluator?.displayName ?? metricInfo?.displayName ?? metricInfo?.name ?? dataSettings.name;

                            // Get the unit to display. If the dataSettings has "unitKey" specified then use that unit. Else, use the unit defined
                            // in the metric definition if the metric definition is a NumericMetric.
                            const unit = (() => {
                                if (dataSettings.unitKey) {
                                    return trans(dataSettings.unitKey);
                                }
                                if (metricInfo && isNumericMetricInfo(metricInfo)) {
                                    return metricInfo.unit;
                                }
                                return undefined;
                            })();

                            // Get the EntityData array for the current entity. Create a new array if one does not exist yet
                            const oneEntityDataArray = result[entity.entityId] ?? [];
                            result[entity.entityId] = oneEntityDataArray;

                            oneEntityDataArray.push({
                                settings: dataSettings,
                                label,
                                value,
                                unit,
                            });
                        }
                    });
                }

                // Add data for the dataSettings that don't have TSDB data. It is possible that the admin create a TSDB dataSettings for a collection
                // that does not exist because the entities can belong to different entity classes. For example, the parent entity has 2 type of
                // entities, Room and Robot. The admin wants to see the robot's "battery" level therefore they added a dataSettings to show
                // "battery" metric. For the "room" entity, this metric does not exist but we still need to return a EntityData object for the
                // metric. Therefore we will loop through all the dataSettings and for each dataSetting that does not have a value in the result,
                // we will add one with "undefined" value.
                multipleDataSettings.forEach((dataSettings) => {
                    if (isTSDBEntityDataSettings(dataSettings)) {
                        // Get the EntityData array for the current entity. Create a new array if one does not exist yet
                        const oneEntityDataArray = result[entity.entityId] ?? [];
                        result[entity.entityId] = oneEntityDataArray;

                        if (!oneEntityDataArray.find((entityData) => {
                            return entityData.settings === dataSettings;
                        })) {
                            oneEntityDataArray.push({
                                settings: dataSettings,
                                label   : dataSettings.labelKey !== undefined ? trans(dataSettings.labelKey) : dataSettings.name,
                                value   : undefined,
                                unit    : undefined,
                            });
                        }
                    }
                });



                // Set the newParamsForFetchingMissingData to remember that we need to fetch these missing data. But we only set it if the
                // newParamsForFetchingMissingData is different from the current paramsForFetchingMissingData
                setParamsForFetchingMissingData((currentParams) => {
                    if (!currentParams
                      || JSON.stringify(sortObjectAttributes(currentParams)) !== JSON.stringify(sortObjectAttributes(newParamsForFetchingMissingData))
                    ) {
                        return newParamsForFetchingMissingData;
                    }
                    return currentParams;
                });
            });

            // If we don't have any missing data to fetch then we can return the result
            if (newParamsForFetchingMissingData.length === 0) {
                return result;
            }
        }

        return undefined;
    }, [appDataState, date, entities, entityClasses, hasErrors, multipleDataSettings, trans]);


    /**
     * This useEffect is used for checking the list of "paramsForFetchingMissingData" to see if there are any actions need to be dispatched
     * and dispatch them.
     */
    useEffect(() => {
        if (paramsForFetchingMissingData && paramsForFetchingMissingData.length > 0) {
            const actions = paramsForFetchingMissingData;
            actions.forEach((action) => {
                if (isFetchMissingCollectionRawDataParams(action)) {
                    appDataDispatch(fetchTimeSeriesCollectionRawData(
                        action.homeId,
                        action.tsdbModuleId,
                        action.collectionId,
                        action.timestamp,
                        -action.sampleSize,
                        action.valueNames,
                        action.tagNames,
                    )).catch(() => {
                        setHasError(true);
                    });
                } else {
                    appDataDispatch(fetchTimeSeriesCollectionData(
                        action.homeId,
                        action.tsdbModuleId,
                        action.collectionId,
                        action.startTime,
                        action.endTime,
                        action.valueNames,
                        action.aggregation,
                        action.resolution,
                    )).catch(() => {
                        setHasError(true);
                    });
                }
            });
        }
    }, [appDataDispatch, paramsForFetchingMissingData, projectId]);

    return multipleEntityData;
};



/**
 * Custom hook to get multiple entity's Alerts counts data. This will require making API call to fetch Alerts data.
 */
const useGetEntitiesAlertCountsData = (
    projectId: number,
    entities: readonly Entity[] | undefined,
    entityClasses: readonly EntityClass[] | undefined,
    multipleDataSettings: readonly EntityDataSettings[] | undefined,
): {
    readonly [entityId: string]: readonly EntityData[] | undefined;
} | undefined => {

    const project = useSelectProject(projectId);
    const { trans } = useRitzTranslation(project);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    // The state to keep track of the params needed for fetching missing data
    const [paramsForFetchingMissingData, setParamsForFetchingMissingData] = useState<readonly UIFetchAlertsFilters[] | undefined>();

    const [hasErrors, setHasErrors] = useState<boolean>(false);


    /**
     * This function will loop through the "multipleDataSettings" array, get the value for each data setting, and return a EntityData object
     * for each data settings.
     * NOTE that this function will only process data settings which has "source" of type "alert_counts"
     */
    const multipleEntityData = useMemo(() => {

        if (entities && entityClasses && multipleDataSettings) {


            // Create a Map of entityId => Array of "EntityDataDisplay" for storing the result for each entity
            const result: {
                [entityId: string]: EntityData[];
            } = {};

            // Create a new array of params for fetching missing data.
            const newParamsForFetchingMissingData: UIFetchAlertsFilters[] = [];

            for (const dataSettings of multipleDataSettings) {

                // This function only handle dataSettings of type AlertCountsEntityDataSettings
                if (isAlertCountsEntityDataSettings(dataSettings)) {

                    entities.forEach((entity) => {

                        // Get the alerts count for the entity
                        const value = (() => {
                            const filters: UIFetchAlertsFilters = {
                                entityId  : entity.entityId,
                                pageNumber: 0,
                                pageSize  : 1,
                                state     : dataSettings.state,
                                severity  : dataSettings.severity,
                            };

                            // Try to get the alerts from cache. If we already have the data from cache, use it. If not, add this to the list
                            // of missing data so that we can fetch it later.
                            const alerts = selectAlerts(appDataState, projectId, filters);
                            if (alerts) {
                                return alerts.range?.total ?? 0;
                            }

                            // alerts data is not available yet. Add a new action to fetch missing data HOWEVER, only do it if we have not
                            // fetched it before. If we have tried fetching data before and got errors then don't create a new action
                            // because it will fail again anyway.
                            if (!hasErrors) {
                                newParamsForFetchingMissingData.push(filters);
                            }

                            return undefined;
                        })();

                        // Get the data's label
                        const label = (() => {
                            // If custom label is provided then use the custom label
                            if (dataSettings.labelKey !== undefined) {
                                return trans(dataSettings.labelKey);
                            }

                            // Else, dataSettings.name
                            return dataSettings.name;
                        })();

                        // Get the EntityData array for the current entity. Create a new array if one does not exist yet
                        const oneEntityDataArray = result[entity.entityId] ?? [];
                        result[entity.entityId] = oneEntityDataArray;

                        oneEntityDataArray.push({
                            settings: dataSettings,
                            label   : label ?? dataSettings.name,
                            value,
                            unit    : undefined,
                        });
                    });
                }
            }


            // Set the newParamsForFetchingMissingData to remember that we need to fetch these missing data. But we only set it if the
            // newParamsForFetchingMissingData is different from the current paramsForFetchingMissingData
            setParamsForFetchingMissingData((currentParams) => {
                if (!currentParams
                  || JSON.stringify(sortObjectAttributes(currentParams)) !== JSON.stringify(sortObjectAttributes(newParamsForFetchingMissingData))
                ) {
                    return newParamsForFetchingMissingData;
                }
                return currentParams;
            });


            // If we don't have any missing data to fetch then we can return whatever we have in the result
            // Else, return undefined and wait for more data to be fetched
            if (newParamsForFetchingMissingData.length === 0) {
                return result;
            }
        }

        return undefined;
    }, [appDataState, entities, entityClasses, hasErrors, multipleDataSettings, projectId, trans]);


    /**
     * This useEffect is used for checking the list of "paramsForFetchingMissingData" to see if there are any actions need to be dispatched
     * and dispatch them.
     */
    useEffect(() => {
        if (paramsForFetchingMissingData && paramsForFetchingMissingData.length > 0) {
            const actions = paramsForFetchingMissingData;
            actions.forEach((action) => {
                appDataDispatch(fetchAlerts(projectId, action)).catch(() => {
                    setHasErrors(true);
                });
            });
        }
    }, [appDataDispatch, paramsForFetchingMissingData, projectId]);

    return multipleEntityData;
};



/**
 * Custom hook to get entity data for all "multipleDataSettings". This function doesn't actually do much. All it does is calling the
 * other function to fetch data for each data source type and then merge the results together.
 */
export const useGetEntitiesData = (
    projectId: number,
    entities: readonly Entity[] | undefined,
    entityClasses: readonly EntityClass[] | undefined,
    multipleDataSettings: readonly EntityDataSettings[] | undefined,
    date: number,
): {
    readonly [entityId: string]: readonly EntityData[] | undefined;
} | undefined => {

    // Fetch the data for each specific type. Each function will ONLY fetch data for the dataSettings of the type the function support
    // For example, "basicAttributeEntityData" will only have the values for the data settings of type "basic_attribute"
    const basicAttributeEntitiesData = useGetEntitiesBasicAttributeData(projectId, entities, entityClasses, multipleDataSettings);
    const customAttributeEntitiesData = useGetEntitiesCustomAttributeData(projectId, entities, entityClasses, multipleDataSettings);
    const descendantCountsEntitiesData = useGetEntitiesDescendantCountsData(projectId, entities, entityClasses, multipleDataSettings);
    const tsdbEntitiesData = useGetEntitiesTSDBData(projectId, entities, entityClasses, multipleDataSettings, date);
    const alertCountsEntitiesData = useGetEntitiesAlertCountsData(projectId, entities, entityClasses, multipleDataSettings);


    const result = useMemo((): {
        readonly [entityId: string]: readonly EntityData[] | undefined;
    } | undefined => {
        if (entities && multipleDataSettings && basicAttributeEntitiesData && customAttributeEntitiesData
            && descendantCountsEntitiesData && tsdbEntitiesData && alertCountsEntitiesData) {

            const result: {
                [entityId: string]: readonly EntityData[] | undefined;
            } = {};

            entities.forEach((entity) => {
                const basicAttributeEntityData = basicAttributeEntitiesData[entity.entityId] ?? [];
                const customAttributeEntityData = customAttributeEntitiesData[entity.entityId] ?? [];
                const descendantCountsEntityData = descendantCountsEntitiesData[entity.entityId] ?? [];
                const tsdbEntityData = tsdbEntitiesData[entity.entityId] ?? [];
                const alertCountsEntityData = alertCountsEntitiesData[entity.entityId] ?? [];

                // Merge the 3 array of data into 1 array. NOTE that the items in these 3 arrays are not in the same order as the
                // data in "multipleDataSettings".
                const mergedData: EntityData[] = [
                    ...basicAttributeEntityData,
                    ...customAttributeEntityData,
                    ...descendantCountsEntityData,
                    ...tsdbEntityData,
                    ...alertCountsEntityData,
                ];

                // Now reorder the result data to the same order as the order in the multipleDataSettings
                const sortedData: EntityData[] = [];

                multipleDataSettings.forEach((dataSettings) => {
                    const data = mergedData.find((data) => {
                        return data.settings === dataSettings;
                    });
                    if (data) {
                        sortedData.push(data);
                    }
                });

                result[entity.entityId] = sortedData;
            });

            return result;

        }
        return undefined;
    }, [entities, multipleDataSettings, basicAttributeEntitiesData, customAttributeEntitiesData, descendantCountsEntitiesData, tsdbEntitiesData,
        alertCountsEntitiesData]);

    return result;
};



export interface EntitySummaryGridUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntitySummaryGridUIComponentCustomSettings | undefined;
}


/**
 * This EntityUIComponent is used for showing a summary of an entity e.g. name, attribute values, tsdb values, etc...
 */
export const EntitySummaryGridUIComponent: React.FC<EntitySummaryGridUIComponentProps> = (props: EntitySummaryGridUIComponentProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const isXLScreen = useScreenSizeCheck('xl');
    const isLGScreen = useScreenSizeCheck('lg');
    const isMDScreen = useScreenSizeCheck('md');
    const isSMScreen = useScreenSizeCheck('sm');
    const isXSScreen = useScreenSizeCheck('xs');


    const classes = useStyle();

    // Because we need to show the latest value for all the data, we will use the current time as the date which we need to fetch data for.
    const date = useMemo(() => {
        return Date.now();
    }, []);


    // Create and cache the array of entities we will pass to the "useGetEntitiesData" hook to fetch data for. Even though there is only 1
    // entity, we still need to cache this array instead of creating a new one dynamically every time we call "useGetEntitiesData". This
    // will prevent the "useGetEntitiesData" form making unnecessary API calls
    const entities = useMemo(() => {
        if (selectedEntity) {
            return [selectedEntity];
        }
        return undefined;
    }, [selectedEntity]);


    // First, call the "useGetEntitiesData" to get the values for all the data we need to display
    const multipleEntitiesData = useGetEntitiesData(
        props.projectId,
        entities,
        entityClasses,
        props.customSettings?.displayData,
        date,
    );

    // The "useGetEntitiesData" fetch data for multiple entities and return the result as a map of entityId => EntityData[].
    // We need to lookup the for the selectedEntity.
    const multipleEntityData = useMemo(() => {
        if (multipleEntitiesData && selectedEntity) {
            return multipleEntitiesData[selectedEntity.entityId];
        }
        return undefined;
    }, [multipleEntitiesData, selectedEntity]);


    /**
     * multipleEntityData is a 1D array. When we display the data, we will display them in a 2D grid. So we need to group these data into
     * a groups of rows/column before we can display them.
     *
     * For example: given 10 data to be shown
     *      multipleEntityData = [A, B, C, D, E, F, G, H, I, J]
     *
     * If the admin doesn't care which column these data are displayed then this is how we will display the data
     *  - This is how we will display the data in 2 columns. Note that we will show the data vertically from top to bottom and then the
     *    column is full, we move to the next column.
     *      A   F
     *      B   G
     *      C   H
     *      D   I
     *      E   J
     *
     *  - This is how we are going to display the data in 3 columns
     *      A   E   I
     *      B   F   J
     *      C   G
     *      D   H
     *
     * However, because the admin can customize the data and have some of the data be shown in a specific column, we will have arrange
     * the data accordingly. We will first put the data that have preferredColumn into the correct column and then process the
     * other data that don't have preferredColumns. For example, let say some of the data have preferredColumn, preferredColumn is 0 based.
     *      multipleEntityData = [A, B, C(1), D, E(1), F, G, H, I, J] // C and E are preferred to be in column 1 (the second column)
     *
     * This is how we will display the data in 2 columns
     *      A   C
     *      B   E
     *      D   H
     *      F   I
     *      G   J
     *
     * This is how we will display the data in 3 columns
     *      A   C   I
     *      B   E   J
     *      D   G
     *      F   H
     */
    const entityDataRows = useMemo((): readonly (EntityData | undefined)[][] | undefined => {
        if (multipleEntityData) {

            // Get the number of columns we will display the data based on the current screen size and the "gridSizes" customSettings
            const columnsCount = ((): number => {
                if (props.customSettings?.gridSizes) {
                    // If grid size is defined then return the number of columns based on the current screen size and gridSize settings
                    if (isXLScreen && props.customSettings.gridSizes.xl && props.customSettings.gridSizes.xl !== 'auto') {
                        return Math.max(1, Math.floor(12 / props.customSettings.gridSizes.xl));
                    }
                    if (isLGScreen && props.customSettings.gridSizes.lg && props.customSettings.gridSizes.lg !== 'auto') {
                        return Math.max(1, Math.floor(12 / props.customSettings.gridSizes.lg));
                    }
                    if (isMDScreen && props.customSettings.gridSizes.md && props.customSettings.gridSizes.md !== 'auto') {
                        return Math.max(1, Math.floor(12 / props.customSettings.gridSizes.md));
                    }
                    if (isSMScreen && props.customSettings.gridSizes.sm && props.customSettings.gridSizes.sm !== 'auto') {
                        return Math.max(1, Math.floor(12 / props.customSettings.gridSizes.sm));
                    }
                    if (isXSScreen && props.customSettings.gridSizes.xs && props.customSettings.gridSizes.xs !== 'auto') {
                        return Math.max(1, Math.floor(12 / props.customSettings.gridSizes.xs));
                    }
                }

                // The component does not have "gridSizes" settings so use the default, 1 column
                return 1;
            })();

            // Create the columns and then add the data to the columns
            const columns = (new Array<(EntityData | undefined)[]>(columnsCount)).fill([]).map((): (EntityData | undefined)[] => {
                return [];
            });


            // first, add the add that have preferredColumn # into the corresponding column. For add that don't have preferredColumn
            // set, add them to the "unusedEntityData" array which will get processed later.
            const unusedEntityData: EntityData[] = [];

            for (const data of multipleEntityData) {
                // make sure the "preferredCol" is within the min and max columns. Because the number of columns is dynamic based on the screen
                // size therefore it is possible that the admin will specify a column number greater than the max columns.
                if (
                    data.settings.preferredColumn !== undefined
                    && data.settings.preferredColumn > 0
                    && data.settings.preferredColumn < columnsCount
                ) {
                    columns[data.settings.preferredColumn].push(data);
                } else {
                    unusedEntityData.push(data);
                }
            }

            // Find out the length of the column that has the most items. We will need this to calculate the number of rows later.
            const columnWithTheMostItems = columns.reduce((result, column) => {
                return Math.max(result, column.length);
            }, 0);


            // Find out how many rows we will have. If none of the data have preferred column and used the default layout then the number of
            // rows will be equal Math.ceil(dataCount / columnsCount). So if there are 10 data and 3 columns then rowsCount = 4.
            // However, because some data have preferred columns, there might be a column that has many data, maybe more than 4.
            // So when we calculate the rowsCount, we need to take into account the number of items in one of the columns. If one of the columns
            // has more than 4 items then the rowsCount needs to be the length of that column, not 4.
            const rowsCount = Math.max(Math.ceil(multipleEntityData.length / columnsCount), columnWithTheMostItems);


            // Now add the rest of the unused data into the columns starting from the first column. We will fill the first columns
            // until it's length reached "numberOfRows" then move on to the next column and so on.
            for (const data of unusedEntityData) {
                // add the data to the column that is not full yet
                columns.find((column) => {
                    if (column.length < rowsCount) {
                        column.push(data);
                        return true;
                    }
                    return false;
                });
            }

            // Convert an array of columns to rows. When we create the HTML grid to display the data for these data, we will create 1 row at a
            // time so we need to convert these columns of data into rows of data so that it is easier to loop through them
            const rows: (EntityData | undefined)[][] = [];
            for (let row = 0; row < rowsCount; row += 1) {
                const newRow: (EntityData | undefined)[] = [];
                rows.push(newRow);
                for (let col = 0; col < columnsCount; col += 1) {
                    const column = columns[col];
                    newRow.push(column[row]);
                }
            }

            return rows;
        }

        return undefined;
    }, [isLGScreen, isMDScreen, isSMScreen, isXLScreen, isXSScreen, multipleEntityData, props.customSettings?.gridSizes]);



    return (
        <>
            {selectedEntity && entityDataRows && (
                <GenericPanelComp
                    className={clsx(props.className, classes.root)}
                    style={{
                        ...props.style,
                        ...props.customSettings?.containerStyle,
                    }}
                    title={props.customSettings?.titleKey ? trans(props.customSettings.titleKey) : undefined}
                    icon={props.customSettings?.icon}
                    headerless={!props.customSettings?.titleKey && !props.customSettings?.icon}
                >
                    <div
                        className={classes.gridContainerStyle}
                        style={props.customSettings?.gridContainerStyle}
                    >
                        <Table className={classes.table}>
                            <TableBody>
                                {entityDataRows.map((row, rowIndex) => {
                                    return (
                                        <TableRow
                                            className={classes.row}
                                            key={`${rowIndex}`}
                                        >
                                            {row.map((data, colIndex) => {

                                                const value = data?.value;

                                                // If dataSettings has decimalPlaces setting then use it. Else use the global decimalPlaces settings
                                                const decimalPlaces = data
                                                    && (
                                                        isCustomAttributeEntityDataSettings(data.settings)
                                                        || isTSDBEntityDataSettings(data.settings)
                                                    )
                                                    && data.settings.decimalPlaces !== undefined
                                                    ? data.settings.decimalPlaces
                                                    : props.customSettings?.decimalPlaces;

                                                const defaultValueKey = data?.settings.defaultValueKey ?? props.customSettings?.defaultValueKey;
                                                const defaultValue = defaultValueKey !== undefined ? trans(defaultValueKey) : NO_VALUE;

                                                return (
                                                    <Fragment
                                                        key={data
                                                            ? `${data.settings.source}-${data.settings.name}-${colIndex}`
                                                            : colIndex}
                                                    >
                                                        <TableCell
                                                            className={clsx(classes.column)}
                                                            style={props.customSettings?.dataContainerStyle}
                                                        >
                                                            {data && (
                                                                <div className={classes.dataDisplay}>
                                                                    <div className="label">{data.label}</div>
                                                                    <div className="value-and-unit">
                                                                        {value !== undefined && typeof value === 'number' && decimalPlaces !== undefined ? (
                                                                            <span className="value">
                                                                                {trans('{{value, number-format}}', {
                                                                                    value,
                                                                                    decimalPlaces,
                                                                                })}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="value">{value ?? defaultValue}</span>
                                                                        )}
                                                                        {data.unit !== undefined && (
                                                                            <span className="unit">{data.unit}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                    </Fragment>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </GenericPanelComp>
            )}

            {!selectedEntity && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntitySummaryGridUIComponent', EntitySummaryGridUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Summary_Grid_Comp',
    {
        componentFunc          : EntitySummaryGridUIComponent,
        customSettingsValidator: isEntitySummaryGridUIComponentCustomSettings,
    },
);
